import React from "react";
import { motion } from "framer-motion";
import SectionHeader from "../components/SectionHeader";

const features = [
  {
    icon: "🚀",
    title: "Lightning-Fast Deployment",
    description: "Go from concept to production in days, not months. Our automated pipelines ensure rapid, reliable deployment.",
    color: "rgba(26,107,255,0.15)"
  },
  {
    icon: "🛡️",
    title: "Enterprise-Grade Security",
    description: "Bank-level encryption, SOC 2 compliance, and 24/7 monitoring keep your data safe and your business protected.",
    color: "rgba(0,180,130,0.15)"
  },
  {
    icon: "📊",
    title: "Real-Time Analytics",
    description: "Track performance, user engagement, and ROI with our comprehensive dashboard and custom reporting tools.",
    color: "rgba(120,70,220,0.15)"
  },
  {
    icon: "🔧",
    title: "No-Code Integration",
    description: "Connect with 500+ tools and services through our extensive API marketplace and pre-built connectors.",
    color: "rgba(240,130,0,0.15)"
  },
  {
    icon: "🌍",
    title: "Global Infrastructure",
    description: "Deploy across 50+ regions worldwide with automatic failover and 99.99% uptime guarantee.",
    color: "rgba(26,107,255,0.15)"
  },
  {
    icon: "🤖",
    title: "AI-Powered Automation",
    description: "Leverage machine learning for predictive scaling, anomaly detection, and intelligent resource optimization.",
    color: "rgba(120,70,220,0.15)"
  }
];

const Features = () => {
  return (
    <section
      id="features"
      className="section-padding"
      style={{ background: "var(--navy)" }}
      aria-labelledby="features-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Platform Features"
          title="Everything You Need to<br/>Scale Your Business"
          subtitle="From startups to enterprises, our platform adapts to your growth with powerful features and seamless integrations."
        />

        <motion.div 
          className="grid gap-8 mt-16"
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(350px,1fr))" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="group relative overflow-hidden rounded-2xl p-8"
              style={{
                background: "linear-gradient(135deg, rgba(15,31,61,0.8), rgba(10,22,40,0.9))",
                border: "1px solid rgba(45,125,255,0.15)"
              }}
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.7, 
                delay: i * 0.1,
                type: "spring",
                stiffness: 80,
                damping: 20
              }}
              whileHover={{ 
                y: -8,
                scale: 1.02,
                borderColor: "rgba(45,125,255,0.4)",
                transition: { duration: 0.3, type: "spring", stiffness: 400 }
              }}
            >
              {/* Hover Glow Effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: feature.color,
                  filter: "blur(20px)",
                  transform: "scale(1.1)"
                }}
              />
              
              <div className="relative z-10">
                {/* Icon */}
                <motion.div
                  initial={{ rotate: 0 }}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-6"
                  style={{
                    background: feature.color,
                    border: "1px solid rgba(255,255,255,0.2)"
                  }}
                >
                  {feature.icon}
                </motion.div>

                <h3
                  className="font-display text-white mb-4 text-xl group-hover:text-blue-400 transition-colors tracking-tight"
                  style={{ letterSpacing: "-0.025em" }}
                >
                  {feature.title}
                </h3>
                
                <p className="text-base leading-relaxed text-gray-300">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <button
            className="btn-primary text-lg px-8 py-4"
            onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
          >
            See All Plans & Features
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
